import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Storefront as StorefrontIcon, Sparkle, Package, MapPin, X, PencilSimple, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import ImageGallery from "@/components/ImageGallery";
import ImageUploader from "@/components/ImageUploader";
import StarRating from "@/components/StarRating";

const DEFAULT_IMG = "https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=800&q=80";

export default function Storefront() {
  const { userId } = useParams();
  const { user } = useAuth();
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/storefronts/${userId}`); setStore(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Store not found"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  if (loading) return <div className="max-w-6xl mx-auto p-12 text-[#5C564D]">Loading storefront…</div>;
  if (!store) return null;

  const isOwner = user && user !== false && user.id === store.seller_id;

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <Link to="/marketplace" data-testid="back-marketplace" className="text-sm text-[#2C4A3B] font-semibold flex items-center gap-1 hover:underline"><ArrowLeft size={14} /> Back to Marketplace</Link>

      <div className="mt-6 samaaj-card p-8 relative overflow-hidden">
        <div className="flex flex-col md:flex-row items-start gap-6">
          <div className="w-24 h-24 rounded-2xl overflow-hidden border border-[#E5D9CC] bg-[#F2EBE5] flex items-center justify-center shrink-0">
            {store.storefront_photo ? (
              <img src={store.storefront_photo} alt={store.storefront_name} className="w-full h-full object-cover" />
            ) : (
              <StorefrontIcon size={38} weight="duotone" className="text-[#D95A41]" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#D49A36]">Community storefront</div>
            <div className="font-heading text-4xl mt-1 text-[#1C1B1A] tracking-tight" data-testid="store-name">{store.storefront_name}</div>
            <div className="text-sm text-[#5C564D] mt-1">Run by {store.name}</div>
            <p className="text-[#1C1B1A] mt-3 leading-relaxed max-w-2xl">{store.storefront_bio || "This seller hasn't added a bio yet."}</p>
            <div className="mt-3">
              <StarRating value={store.avg_rating || 0} count={store.review_count} showLabel size={16} />
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="samaaj-chip">Total {store.counts.total}</span>
              <span className="samaaj-chip" style={{ background: "#DDEBDF", color: "#2C4A3B" }}><Sparkle size={12} weight="fill" /> {store.counts.fresh} fresh</span>
              <span className="samaaj-chip" style={{ background: "#EFE4D3" }}><Package size={12} /> {store.counts.second_hand} used</span>
            </div>
          </div>
          {isOwner && (
            <button data-testid="btn-edit-store" onClick={() => setEditOpen(true)} className="samaaj-btn-outline text-sm flex items-center gap-1.5">
              <PencilSimple size={14} /> Edit store
            </button>
          )}
        </div>
      </div>

      <div className="mt-8">
        <div className="text-xs tracking-[0.15em] uppercase font-bold text-[#2C4A3B]">Listings</div>
        {store.listings.length === 0 ? (
          <div className="samaaj-card p-10 text-center mt-4">
            <StorefrontIcon size={32} className="mx-auto text-[#D95A41]" />
            <div className="font-heading text-2xl mt-3">Nothing on the shelves yet</div>
            <p className="text-[#5C564D] mt-2">{isOwner ? "Head to the Marketplace and add your first listing." : "Check back soon — this seller hasn't posted yet."}</p>
          </div>
        ) : (
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {store.listings.map((item) => {
              const isFresh = item.condition_type === "fresh";
              return (
                <div key={item.id} className="samaaj-card overflow-hidden">
                  <div className="aspect-[4/3] relative">
                    <ImageGallery images={item.images} fallback={item.image_url || DEFAULT_IMG} alt={item.title} />
                    {item.status === "sold" && <div className="absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-bold text-[#F9F6F0] bg-[#2C4A3B]">Sold</div>}
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider flex items-center gap-1"
                        style={{ background: isFresh ? "#DDEBDF" : "#F2EBE5", color: isFresh ? "#2C4A3B" : "#5C564D" }}>
                        {isFresh ? <><Sparkle size={10} weight="fill" /> Fresh</> : <><Package size={10} /> Second-hand</>}
                      </span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-bold text-[#F9F6F0]" style={{ background: "#D49A36" }}>{item.category}</span>
                    </div>
                    <div className="font-heading text-xl mt-2">{item.title}</div>
                    <div className="text-xs text-[#5C564D] mt-1 flex items-center gap-1"><MapPin size={12} /> {item.location}</div>
                    <div className="mt-3 font-heading text-2xl text-[#D95A41]">${(+item.price).toFixed(2)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {editOpen && <EditStore initial={store} onClose={() => setEditOpen(false)} onSaved={() => { setEditOpen(false); load(); }} />}

      {store.recent_reviews && store.recent_reviews.length > 0 && (
        <div className="mt-10" data-testid="store-reviews">
          <div className="text-xs tracking-[0.15em] uppercase font-bold text-[#D49A36]">Community reviews</div>
          <div className="mt-4 grid sm:grid-cols-2 gap-4">
            {store.recent_reviews.map((r) => (
              <div key={r.id} className="samaaj-card p-4">
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-[#1C1B1A]">{r.buyer_name || "Community buyer"}</div>
                  <StarRating value={r.rating} size={12} />
                </div>
                {r.comment && <p className="text-sm text-[#5C564D] mt-2 leading-relaxed">"{r.comment}"</p>}
                <div className="text-[10px] text-[#5C564D] mt-2">{new Date(r.created_at).toLocaleDateString()}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function EditStore({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    storefront_name: initial.storefront_name || "",
    storefront_bio: initial.storefront_bio || "",
    storefront_photo: initial.storefront_photo || "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.put("/users/me", form);
      toast.success("Store updated");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#1C1B1A]/60 flex items-center justify-center p-4 overflow-y-auto">
      <div className="samaaj-card max-w-lg w-full p-6 bg-[#F9F6F0] my-8">
        <div className="flex items-start justify-between">
          <div className="font-heading text-3xl">Edit your storefront</div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[#F2EBE5]"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="mt-4 space-y-3">
          <input data-testid="store-name-input" placeholder="Store name" value={form.storefront_name} onChange={(e) => setForm({ ...form, storefront_name: e.target.value })} className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC]" />
          <textarea data-testid="store-bio-input" placeholder="Short bio — who you are, what you make/sell" rows={5} value={form.storefront_bio} onChange={(e) => setForm({ ...form, storefront_bio: e.target.value })} className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC]" />
          <ImageUploader testid="store-photo" label="Store photo (logo or hero)" value={form.storefront_photo} onChange={(url) => setForm({ ...form, storefront_photo: url })} />
          <button data-testid="store-save" disabled={busy} className="samaaj-btn-primary w-full">{busy ? "Saving…" : "Save store"}</button>
        </form>
      </div>
    </div>
  );
}
